import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";

// A labelled pill-group choice for Settings (units kg/lb, effort RIR/RPE,
// language, …). The interaction lives here once; mobile's UnitsPicker /
// EffortScalePicker and the web console's Preferences both render it, so
// the pill UI stops drifting between platforms. Data orchestration (which
// profile field, how it's saved) stays in the mount — this is display +
// signal only.
//
// a11y: active pill carries a "✓ " glyph + accessibilityState.selected,
// never colour alone; every pill is ≥44pt tall.

export type PillOption<T extends string> = { value: T; label: string };

export type SettingsPillChoiceProps<T extends string> = {
  theme: UiTheme;
  label: string;
  help?: string;
  options: PillOption<T>[];
  value: T | null;
  onPick: (value: T) => void;
  saving?: boolean;
  error?: string | null;
  disabled?: boolean;
  /** Many short options (e.g. age buckets) → a tight wrapping grid instead of wide pills. */
  compact?: boolean;
};

export function SettingsPillChoice<T extends string>({
  theme,
  label,
  help,
  options,
  value,
  onPick,
  saving = false,
  error = null,
  disabled = false,
  compact = false,
}: SettingsPillChoiceProps<T>) {
  const styles = makeStyles(theme);
  const blocked = saving || disabled;

  return (
    <View style={styles.card}>
      <Text style={styles.label}>{label}</Text>
      {help ? <Text style={styles.help}>{help}</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <View style={styles.pillRow}>
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <Pressable
              key={opt.value}
              accessibilityRole="button"
              accessibilityState={{ selected: active, disabled: blocked }}
              disabled={blocked}
              onPress={() => onPick(opt.value)}
              hitSlop={8}
              style={({ pressed }) => [
                styles.pill,
                compact && styles.pillCompact,
                active && styles.pillActive,
                pressed && styles.pillPressed,
                blocked && styles.pillDisabled,
              ]}
            >
              <Text
                style={[
                  styles.pillText,
                  compact && styles.pillTextCompact,
                  active && styles.pillTextActive,
                ]}
              >
                {active ? "✓ " : ""}
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
      {saving ? <ActivityIndicator color={theme.palette.ink2} /> : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s2,
    },
    label: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      letterSpacing: 0.2,
    },
    help: { color: p.ink3, fontSize: t.sizes.sm },
    error: { color: p.danger, fontSize: t.sizes.base },
    pillRow: { flexDirection: "row", flexWrap: "wrap", gap: s.s3, marginTop: s.s2 },
    pill: {
      flexGrow: 1,
      flexBasis: 120,
      minHeight: 36,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.borderStrong,
      backgroundColor: p.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    pillCompact: { flexGrow: 0, flexBasis: "auto", minWidth: 44, paddingHorizontal: s.s3 },
    pillActive: { borderColor: p.brand500, backgroundColor: p.brand500 },
    pillPressed: { opacity: 0.7 },
    pillDisabled: { opacity: 0.5 },
    pillText: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    pillTextCompact: { fontSize: t.sizes.sm },
    pillTextActive: { color: p.inkOnBrand },
  });
}
